import { useState } from "react";
import { Link } from "react-router-dom";

interface Product {
  id: string;
  title: string;
  price: number;
  originalPrice: number;
  image: string;
  rating: number;
  reviews: number;
  commission: number;
  badge: string;
  stock: number;
  warehouse: string[];
}

interface FactoryProductsProps {
  products: Product[];
  factoryName: string;
}

export default function FactoryProducts({ products, factoryName }: FactoryProductsProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [wishlist, setWishlist] = useState<string[]>([]);

  const handleCopyReferral = (productId: string) => {
    const link = `${window.location.origin}/product/${productId}?ref=myhandle`;
    navigator.clipboard.writeText(link);
    setCopiedId(productId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleWishlist = (id: string) => {
    setWishlist((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-extrabold text-gray-900">
          Products <span className="text-gray-400 font-normal text-base">({products.length})</span>
        </h2>
        <span className="text-xs text-gray-500 bg-rose-50 border border-rose-100 px-3 py-1.5 rounded-full font-medium">
          <i className="ri-star-line text-rose-500 mr-1" />Commission set by factory per product
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop>
        {products.map((product) => (
          <div key={product.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-orange-200 transition-all group">
            {/* Image */}
            <div className="relative aspect-square overflow-hidden">
              <img src={product.image} alt={product.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute top-2 left-2 flex flex-col gap-1">
                <span className="flex items-center gap-1 bg-white/90 text-green-600 text-xs font-semibold px-2 py-1 rounded-full">
                  <i className="ri-shield-check-fill" /> Verified
                </span>
                {product.badge && (
                  <span className="bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded-full">{product.badge}</span>
                )}
              </div>
              <button onClick={() => toggleWishlist(product.id)} className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full cursor-pointer hover:bg-white">
                <i className={wishlist.includes(product.id) ? "ri-heart-fill text-red-500" : "ri-heart-line text-gray-400"} />
              </button>
              {/* Warehouse badges */}
              <div className="absolute bottom-2 left-2 flex gap-1">
                {product.warehouse.includes("local") && (
                  <span className="bg-green-500/90 text-white text-xs font-semibold px-2 py-0.5 rounded-full">🇺🇸 Local</span>
                )}
                {product.warehouse.includes("overseas") && (
                  <span className="bg-orange-500/90 text-white text-xs font-semibold px-2 py-0.5 rounded-full">🌏 Overseas</span>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">{product.title}</h3>
              <div className="flex items-center gap-1 mb-2">
                <span className="text-yellow-400 text-xs">{"★".repeat(Math.floor(product.rating))}</span>
                <span className="text-xs text-gray-400">({product.reviews.toLocaleString()})</span>
              </div>

              {/* Price */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg font-bold text-gray-900">${product.price}</span>
                <span className="text-sm text-gray-400 line-through">${product.originalPrice}</span>
                <span className="text-xs text-green-600 font-semibold">{Math.round((1 - product.price / product.originalPrice) * 100)}% off</span>
              </div>

              {/* Commission Banner — KEY FEATURE */}
              <div className="bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-200 rounded-lg px-3 py-2.5 mb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Reseller Commission</p>
                    <p className="text-lg font-extrabold text-rose-600">{product.commission}%</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">You earn per sale</p>
                    <p className="text-sm font-bold text-green-600">
                      +${(product.price * product.commission / 100).toFixed(2)}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">Set by {factoryName} · Paid via PayPal</p>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Link
                  to={`/product/${product.id}`}
                  className="flex-1 py-2.5 bg-orange-500 text-white text-xs font-bold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-center"
                >
                  View Product
                </Link>
                <button
                  onClick={() => handleCopyReferral(product.id)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-bold transition-colors cursor-pointer whitespace-nowrap border ${copiedId === product.id ? "bg-green-500 text-white border-green-500" : "border-rose-300 text-rose-600 hover:bg-rose-50"}`}
                >
                  <i className={copiedId === product.id ? "ri-checkbox-circle-fill" : "ri-link-m"} />
                  {copiedId === product.id ? "Copied!" : "Get Link"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
